import { useState } from 'react'
import { Helmet } from 'react-helmet-async'
import Preloader from '../components/Preloader'
import Header from '../components/Header'
import ScrollReveal from '../components/ScrollReveal'
import Footer from '../components/Footer'
import { AiFillProduct } from 'react-icons/ai'
import { TbAugmentedReality } from 'react-icons/tb'
import { GrCatalog } from 'react-icons/gr'
import { MdOutlineEventNote } from 'react-icons/md'
import { FaRocket, FaChartLine, FaMobileAlt } from 'react-icons/fa'

const vrSections = [
    {
        title: '3D Product Visualization',
        icon: <AiFillProduct className="text-3xl text-yellow-300" />,
        description:
            'Allows your customers to see how a product looks in its real environment before buying it. Ideal for ecommerce, retail, and real estate experiences.'
    },
    {
        title: 'Social Augmented Reality Filters',
        icon: <TbAugmentedReality className="text-3xl text-yellow-300" />,
        description:
            'Businesses can develop custom filters for platforms like Instagram and TikTok to increase brand engagement, reach, and shareability.'
    },
    {
        title: 'Interactive Catalogue',
        icon: <GrCatalog className="text-3xl text-yellow-300" />,
        description:
            'Brochures and catalogs are transformed into immersive digital experiences with animations, 3D models, and guided interactions.'
    },
    {
        title: 'Augmented Reality for Events',
        icon: <MdOutlineEventNote className="text-3xl text-yellow-300" />,
        description:
            'Event booths and activations can be made more interactive, memorable, and visually impactful with AR overlays and experiences.'
    }
]

const stats = [
    { value: '4x', label: 'more engagement' },
    { value: '24/7', label: 'interactive visibility' },
    { value: '100%', label: 'mobile-ready overlays' },
    { value: 'Fast', label: 'prototype delivery' }
]

const highlights = [
    {
        title: 'Immersive storytelling',
        description: 'Turn physical spaces into memorable experiences that help audiences understand products and services instantly.',
        icon: <FaRocket className="text-2xl text-yellow-300" />
    },
    {
        title: 'Practical business use cases',
        description: 'From retail and education to events and industry, AR helps businesses communicate value in a more tangible way.',
        icon: <FaChartLine className="text-2xl text-yellow-300" />
    },
    {
        title: 'Responsive by design',
        description: 'Every AR experience is built to feel polished across mobile screens, tablets, and desktops.',
        icon: <FaMobileAlt className="text-2xl text-yellow-300" />
    }
]

const AugmentedReality = () => {
    const [expanded, setExpanded] = useState({})

    const toggleExpand = (idx) => {
        setExpanded((prev) => ({
            ...prev,
            [idx]: !prev[idx]
        }))
    }

    return (
        <div>
            <Helmet>
                <title>Augmented Reality | VRIFY Technologies</title>
                <meta
                    name="description"
                    content="Modern augmented reality solutions for retail, education, events, and business growth from VRIFY Technologies."
                />
            </Helmet>

            <div className="font-nanum-myeongjo min-h-screen bg-slate-950 text-slate-100 pt-16 md:pt-20 lg:pt-16 relative overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(250,204,21,0.2),_transparent_24%),radial-gradient(circle_at_bottom_right,_rgba(59,130,246,0.15),_transparent_22%)]" />

                <div className="relative z-10">
                    <Header />
                    <Preloader />

                    <section className="mx-auto max-w-7xl px-4 pb-10 pt-8 sm:px-6 lg:px-8 lg:pb-16">
                        <ScrollReveal>
                            <div className="grid items-center gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:gap-12">
                                <div>
                                    <p className="inline-flex items-center gap-2 rounded-full border border-yellow-400/50 bg-yellow-400/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.3em] text-yellow-200">
                                        <TbAugmentedReality className="text-sm" />
                                        Augmented Reality
                                    </p>
                                    <h1 className="mt-5 text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
                                        Create interactive experiences that bridge the physical and digital world.
                                    </h1>
                                    <p className="mt-4 max-w-2xl text-sm leading-7 text-slate-200 sm:text-base">
                                        VRIFY Technologies helps brands and organizations turn everyday environments into memorable digital experiences using tailored AR solutions built for clarity, engagement, and impact.
                                    </p>

                                    <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                                        <a
                                            href="/contactus"
                                            className="inline-flex items-center justify-center rounded-full bg-yellow-400 px-5 py-3 text-sm font-bold text-slate-950 transition hover:bg-yellow-300"
                                        >
                                            Book a consultation
                                        </a>
                                        <a
                                            href="#ar-solutions"
                                            className="inline-flex items-center justify-center rounded-full border border-slate-700 px-5 py-3 text-sm font-semibold text-white transition hover:border-yellow-300 hover:text-yellow-200"
                                        >
                                            Explore solutions
                                        </a>
                                    </div>

                                    <div className="mt-8 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                                        {stats.map((stat) => (
                                            <div
                                                key={stat.label}
                                                className="rounded-2xl border border-white/10 bg-white/5 px-4 py-4 backdrop-blur-sm"
                                            >
                                                <p className="text-xl font-bold text-white">{stat.value}</p>
                                                <p className="mt-1 text-[11px] uppercase tracking-[0.2em] text-slate-300">
                                                    {stat.label}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="rounded-[28px] border border-white/10 bg-slate-900/85 p-5 shadow-2xl shadow-black/30 backdrop-blur sm:p-6">
                                    <div className="rounded-[24px] bg-gradient-to-br from-yellow-400 via-amber-300 to-slate-100 p-[1px]">
                                        <div className="rounded-[23px] bg-slate-950/95 p-5 sm:p-6">
                                            <p className="text-[11px] uppercase tracking-[0.3em] text-yellow-300">Why teams choose AR</p>
                                            <div className="mt-5 space-y-4">
                                                {highlights.map((item) => (
                                                    <div
                                                        key={item.title}
                                                        className="rounded-2xl border border-white/10 bg-white/5 p-4"
                                                    >
                                                        <div className="flex items-start gap-3">
                                                            <div className="mt-0.5 rounded-xl bg-slate-900 p-2">{item.icon}</div>
                                                            <div>
                                                                <p className="text-sm font-semibold text-white">{item.title}</p>
                                                                <p className="mt-1 text-sm leading-6 text-slate-300">
                                                                    {item.description}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </ScrollReveal>
                    </section>

                    <section id="ar-solutions" className="mx-auto max-w-7xl px-4 pb-10 sm:px-6 lg:px-8">
                        <ScrollReveal>
                            <div className="flex flex-col gap-3 text-center sm:text-left">
                                <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-yellow-300">Core services</p>
                                <h2 className="text-2xl font-bold text-white sm:text-3xl">Built for modern brands, education, and events.</h2>
                                <p className="max-w-2xl text-sm leading-7 text-slate-300 sm:text-base">
                                    Each solution is designed to be immersive, easy to understand, and memorable across every device your audience uses.
                                </p>
                            </div>
                        </ScrollReveal>

                        <div className="mt-8 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                            {vrSections.map((section, idx) => (
                                <ScrollReveal key={section.title}>
                                    <div className="flex h-full flex-col rounded-[24px] border border-white/10 bg-white/5 p-5 shadow-lg shadow-black/20">
                                        <div className="flex justify-center rounded-2xl bg-slate-900/80 p-4">
                                            {section.icon}
                                        </div>
                                        <h3 className="mt-4 text-lg font-semibold text-white">{section.title}</h3>
                                        <p className="mt-3 flex-1 text-sm leading-7 text-slate-300">
                                            {expanded[idx] ? section.description : `${section.description.slice(0, 120)}...`}
                                        </p>
                                        <button
                                            type="button"
                                            onClick={() => toggleExpand(idx)}
                                            className="mt-5 inline-flex items-center justify-center rounded-full bg-yellow-400 px-4 py-2 text-sm font-bold text-slate-950 transition hover:bg-yellow-300"
                                        >
                                            {expanded[idx] ? 'See Less' : 'See More'}
                                        </button>
                                    </div>
                                </ScrollReveal>
                            ))}
                        </div>
                    </section>

                    <section className="mx-auto max-w-7xl px-4 pb-10 sm:px-6 lg:px-8">
                        <ScrollReveal>
                            <div className="rounded-[30px] border border-yellow-300/30 bg-gradient-to-r from-yellow-400 via-amber-300 to-yellow-200 px-5 py-8 sm:px-8 lg:flex lg:items-center lg:justify-between lg:px-10">
                                <div className="max-w-2xl">
                                    <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-slate-950">Impact-focused AR</p>
                                    <h2 className="mt-3 text-2xl font-bold text-slate-950 sm:text-3xl">
                                        Transform how your audience engages with your products, campaigns, and spaces.
                                    </h2>
                                    <p className="mt-3 text-sm leading-7 text-slate-900/80 sm:text-base">
                                        Augmented Reality lets you showcase value instantly, encourage interaction, and give customers a reason to remember your brand.
                                    </p>
                                </div>
                                <a
                                    href="/contactus"
                                    className="mt-6 inline-flex items-center justify-center rounded-full bg-slate-950 px-5 py-3 text-sm font-bold text-yellow-200 transition hover:bg-slate-900 lg:mt-0"
                                >
                                    Start your AR project
                                </a>
                            </div>
                        </ScrollReveal>
                    </section>
                </div>
            </div>

            <Footer />
        </div>
    )
}

export default AugmentedReality